







































































































































import {
    defineComponent,
    PropType,
    ssrRef,
    ref,
    useFetch,
    onMounted,
    computed,
    provide,
} from "@nuxtjs/composition-api";
import LazyHydrate from "vue-lazy-hydration";
import {
    SfSelect,
    SfBreadcrumbs,
    SfHeading,
    SfLoader,
    SfButton,
} from "@storefront-ui/vue";
import { useFacet } from "@wemade-vsf/catalog/lib/runtime/category/composables/useFacet";
import { usePrice } from "@wemade-vsf/catalog/lib/runtime/pricing/usePrice";
import { useUiHelpers, useUiState } from "@wemade-vsf/composables";
import { useAddToCart } from "@wemade-vsf/checkout";
import { useWishlist } from "@wemade-vsf/wishlist";
import { CacheTagPrefix, useCache } from "@wemade-vsf/cache";
import facetGetters from "@wemade-vsf/catalog/lib/runtime/category/getters/facetGetters";
import CategoryNavbar from "components/catalog/Category/CategoryNavbar.vue";
import CategoryPagination from "components/catalog/Category/CategoryPagination.vue";

import type { SortingModel } from "@wemade-vsf/catalog/lib/runtime/category/composables/useFacet/sortingOptions";
import type { Pagination } from "@wemade-vsf/core";
import type { Product } from "@wemade-vsf/catalog/lib/runtime/product/types";
import type { ProductInterface, Aggregation } from "@wemade-vsf/magento-api";

type StaticFilterParams = Record<string, string[]>;

export default defineComponent({
    name: "FilterableProductsLayout",
    props: {
        filter: {
            type: Object as PropType<StaticFilterParams>,
            default: () => ({}),
        },
        ignoreAggregations: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        breadcrumbs: {
            type: [Array, null],
            default: null,
        },
        title: {
            type: String,
            default: "",
        },
        showSearch: {
            type: Boolean,
            default: false,
        },
    },
    transition: "fade",
    components: {
        CategoryPagination,
        LazyHydrate,
        SfSelect,
        SfBreadcrumbs,
        SfHeading,
        CategoryNavbar,
        SfButton,
        SfLoader,
        CategoryFilters: () =>
            import("components/catalog/Category/Filters/CategoryFilters.vue"),
        CategoryProductGrid: () =>
            import("components/catalog/Category/CategoryProductGrid.vue"),
        CategoryProductList: () =>
            import("components/catalog/Category/CategoryProductList.vue"),
        CategoryEmptyResults: () =>
            import("components/catalog/Category/CategoryEmptyResults.vue"),
        CategorySearchForm: () =>
            import("components/catalog/Category/CategorySearchForm.vue"),
    },
    setup(props) {
        const { addTags } = useCache();
        const uiHelpers = useUiHelpers();
        const products = ssrRef<ProductInterface[]>([]);
        const aggregations = ssrRef<Aggregation[]>([]);
        const pagination = ref<Pagination>({});
        const productContainerElement = ref<HTMLElement | null>(null);
        const sortBy = ref<SortingModel>({ selected: "", options: [] });
        const term = ref<string>("");

        const productsLeft = computed(() => {
            if (
                !pagination.value?.totalItems ||
                !pagination.value.currentPage ||
                !pagination.value.itemsPerPage
            )
                return 0;
            const leftProducts =
                pagination.value.totalItems -
                pagination.value.currentPage * pagination.value.itemsPerPage;
            return leftProducts > 0 ? leftProducts : 0;
        });

        const { toggleFilterSidebar, isCategoryGridView, isFilterSidebarOpen } =
            useUiState();

        const categoryViewComponent = computed(() =>
            isCategoryGridView.value
                ? "CategoryProductGrid"
                : "CategoryProductList"
        );

        const { addItemToCart } = useAddToCart();
        const {
            load: loadWishlist,
            addItem: addItemToWishlistBase,
            isInWishlist,
            removeItem: removeItemFromWishlist,
        } = useWishlist();

        const addItemToWishlist = async (product: Product) => {
            (await isInWishlist({ product }))
                ? removeItemFromWishlist({ product })
                : addItemToWishlistBase({ product });
        };

        const {
            result,
            search,
            loadMoreAggregationOptions,
            loading: searchLoading,
        } = useFacet();

        const { fetch } = useFetch(async () => {
            const params = uiHelpers.getFacetsFromURL();
            params.filters = params.filters
                ? {
                      ...params.filters,
                      ...props.filter,
                  }
                : {};
            await search(params);
            term.value = params.term ?? "";

            products.value = facetGetters.getProducts(result.value) ?? [];
            aggregations.value = facetGetters.getAggregations(result.value)
                ? facetGetters
                      .getAggregations(result.value)
                      .filter(
                          (a) =>
                              !props.ignoreAggregations.includes(
                                  a.attribute_code
                              )
                      )
                : [];
            pagination.value = facetGetters.getPagination(result.value);
            sortBy.value = facetGetters.getSortOptions(result.value);

            const productTags = products.value.map((product) => ({
                prefix: CacheTagPrefix.Product,
                value: product.uid,
            }));
            addTags([...productTags]);
        });

        const isPriceLoaded = ref(false);

        onMounted(async () => {
            const { getPricesBySku } = usePrice();

            if (products.value.length > 0) {
                const skus = products.value.map((item) => item.sku);
                const priceData = await getPricesBySku(
                    skus,
                    pagination.value.itemsPerPage
                );
                products.value = products.value.map((product) => {
                    const priceRange = priceData.items.find(
                        (item) => item.sku === product.sku
                    )?.price_range;

                    if (priceRange) {
                        return {
                            ...product,
                            price_range: priceRange,
                        };
                    }

                    return { ...product };
                });
            }

            isPriceLoaded.value = true;
        });

        const loadNextPage = async () => {
            const currentPage = pagination.value?.currentPage || 1;
            uiHelpers.changePage(currentPage + 1, false);
            const params = uiHelpers.getFacetsFromURL();
            params.filters = params.filters
                ? {
                      ...params.filters,
                      ...props.filter,
                  }
                : {};
            await search(params);
            products.value = [
                ...products.value,
                ...(facetGetters.getProducts(result.value) ?? []),
            ];
            pagination.value = facetGetters.getPagination(result.value);
        };

        const goToPage = (page: number) => {
            uiHelpers.changePage(page, false);
            fetch();
        };

        const doChangeItemsPerPage = (itemsPerPage: number) => {
            uiHelpers.changeItemsPerPage(itemsPerPage, false);
            goToPage(0);
        };

        const onReloadProducts = () => {
            goToPage(0);
            productContainerElement.value.scrollIntoView();
        };

        const { getFacetsFromURL } = useUiHelpers();

        const loadMoreOptions = async (attributeCode) => {
            const aggIndex = aggregations.value?.findIndex(
                (a) => a.attribute_code === attributeCode
            );
            if (!aggIndex) return;
            let result = await loadMoreAggregationOptions(
                attributeCode,
                getFacetsFromURL()
            );
            if (result) {
                let currentAggregations = aggregations.value;
                currentAggregations.splice(aggIndex, 1, result);
                aggregations.value = currentAggregations;
            }
        };

        async function doSearch(search: string) {
            await uiHelpers.changeSearchTerm(search);
            fetch();
        }
        provide("UseAggregationProvider", { loadMoreOptions });

        return {
            addItemToCart,
            addItemToWishlist,
            categoryViewComponent,
            isPriceLoaded,
            ...uiHelpers,
            doChangeItemsPerPage,
            goToPage,
            productContainerElement,
            products,
            aggregations,
            toggleFilterSidebar,
            isFilterSidebarOpen,
            pagination,
            onReloadProducts,
            sortBy,
            term,
            doSearch,
            productsLeft,
            loadNextPage,
            searchLoading,
        };
    },
});
